import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Sparkles, Send, Loader2, Copy, Check, Bug, MessageSquareText, FileText, Info, RefreshCw, LineChart } from 'lucide-react';
import { ai } from '../../lib/api';
import { aiFailure, aiRequestError } from '../../lib/aiResult';
import KnowledgeAutoUpdate from './KnowledgeAutoUpdate';
import BusinessBriefing from './BusinessBriefing';
import { toast } from 'sonner';

const GREEN = '#8dd110';
const myRole = () => { try { return (JSON.parse(localStorage.getItem('pp_user') || '{}').role) || ''; } catch { return ''; } };

function CopyBtn({ text }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); }
    catch { toast.error('Could not copy'); }
  };
  return (
    <Button variant="outline" size="sm" onClick={copy} className="gap-1.5">
      {done ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}{done ? 'Copied' : 'Copy'}
    </Button>
  );
}

function Output({ result }) {
  if (!result) return null;
  // A call that failed used to be badged "Template (add an AI key)", which is
  // wrong whenever a key is set and sends the user to fix the wrong thing.
  const failed = result.source === 'error';
  const why = aiFailure(result);
  return (
    <Card className="p-4 border-slate-200 bg-slate-50/60">
      <div className="flex items-center justify-between mb-2">
        <Badge className={`border-0 ${result.source === 'ai' ? 'bg-[#8dd110]/20 text-[#5b8523]' : failed ? 'bg-amber-100 text-amber-800' : 'bg-slate-200 text-slate-600'}`}>
          {result.source === 'ai' ? `AI (${result.model || 'model'})` : failed ? 'Not generated' : 'Template (add an AI key for smarter output)'}
        </Badge>
        <CopyBtn text={result.text} />
      </div>
      {why && <div className="mb-2 text-sm text-amber-800">{why}</div>}
      <pre className="whitespace-pre-wrap font-sans text-sm text-slate-800 leading-relaxed">{result.text}</pre>
    </Card>
  );
}

function AskTab() {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    if (!q.trim()) return;
    setBusy(true); setRes(null);
    try { setRes(await ai.assistant(q.trim())); }
    catch (e) { toast.error(aiRequestError(e, 'Could not get an answer')); }
    finally { setBusy(false); }
  };
  const examples = ['How do I prep for a cockroach treatment?', 'Are black widows dangerous?', 'Why do you bait ants instead of spraying?', 'What is IPM?'];
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-500">Ask anything about pests, prep, or policy — answers are grounded in your Pest Library &amp; Knowledge Base.</p>
      <div className="flex flex-wrap gap-2">
        {examples.map(e => <button key={e} onClick={() => setQ(e)} className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200">{e}</button>)}
      </div>
      <div className="flex gap-2">
        <Input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && run()} placeholder="Type a pest question…" className="h-11" />
        <Button onClick={run} disabled={busy} className="h-11 px-5 text-white gap-1.5" style={{ backgroundColor: GREEN }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Ask
        </Button>
      </div>
      <Output result={res} />
    </div>
  );
}

function DraftTab() {
  const [f, setF] = useState({ kind: 'email', customerName: '', tone: 'friendly and professional', context: '' });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    if (!f.context.trim()) { toast.error('Add a few words about what to say'); return; }
    setBusy(true); setRes(null);
    try { setRes(await ai.draft(f)); }
    catch (e) { toast.error(aiRequestError(e, 'Could not draft the message')); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-500">Draft a customer message — email, SMS, review reply, or a post-service follow-up.</p>
      <div className="grid sm:grid-cols-3 gap-3">
        <div><Label className="text-xs">Type</Label>
          <Select value={f.kind} onValueChange={v => setF({ ...f, kind: v })}><SelectTrigger className="mt-1 h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="email">Email</SelectItem><SelectItem value="sms">SMS text</SelectItem>
              <SelectItem value="review_reply">Review reply</SelectItem><SelectItem value="followup">Service follow-up</SelectItem>
            </SelectContent></Select></div>
        <div><Label className="text-xs">Customer name</Label><Input value={f.customerName} onChange={e => setF({ ...f, customerName: e.target.value })} className="mt-1 h-10" placeholder="(optional)" /></div>
        <div><Label className="text-xs">Tone</Label><Input value={f.tone} onChange={e => setF({ ...f, tone: e.target.value })} className="mt-1 h-10" /></div>
      </div>
      <div><Label className="text-xs">What should it say?</Label>
        <textarea value={f.context} onChange={e => setF({ ...f, context: e.target.value })} rows={4} className="mt-1 w-full text-sm border border-slate-200 rounded-md p-2" placeholder="e.g. Reschedule Tuesday's visit to Thursday 10am; remind them to secure pets." /></div>
      <Button onClick={run} disabled={busy} className="text-white gap-1.5" style={{ backgroundColor: GREEN }}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}Draft message
      </Button>
      <Output result={res} />
    </div>
  );
}

function SummarizeTab() {
  const [f, setF] = useState({ customer: '', service: '', pests: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    if (!f.notes.trim()) { toast.error('Paste the field notes first'); return; }
    setBusy(true); setRes(null);
    try { setRes(await ai.summarize(f)); }
    catch (e) { toast.error(aiRequestError(e, 'Could not summarize')); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-500">Turn a technician's rough field notes into a clean, customer-ready service report.</p>
      <div className="grid sm:grid-cols-3 gap-3">
        <div><Label className="text-xs">Customer</Label><Input value={f.customer} onChange={e => setF({ ...f, customer: e.target.value })} className="mt-1 h-10" placeholder="(optional)" /></div>
        <div><Label className="text-xs">Service</Label><Input value={f.service} onChange={e => setF({ ...f, service: e.target.value })} className="mt-1 h-10" placeholder="(optional)" /></div>
        <div><Label className="text-xs">Target pests</Label><Input value={f.pests} onChange={e => setF({ ...f, pests: e.target.value })} className="mt-1 h-10" placeholder="(optional)" /></div>
      </div>
      <div><Label className="text-xs">Field notes</Label>
        <textarea value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} rows={5} className="mt-1 w-full text-sm border border-slate-200 rounded-md p-2" placeholder="e.g. sprayed perimeter, found roach harborage under sink, set 2 bait stations, recommend follow-up 2 wks" /></div>
      <Button onClick={run} disabled={busy} className="text-white gap-1.5" style={{ backgroundColor: GREEN }}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}Generate report
      </Button>
      <Output result={res} />
    </div>
  );
}

export default function AiAssistant() {
  const [tab, setTab] = useState('ask');
  const [status, setStatus] = useState(null);

  const load = useCallback(async () => {
    try { setStatus(await ai.status()); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const tabs = [['ask', 'Ask', Bug], ['draft', 'Draft Message', MessageSquareText], ['summarize', 'Summarize Notes', FileText]];
  if (myRole() === 'admin') tabs.push(['briefing', 'Briefing', LineChart], ['update', 'Auto-Update', RefreshCw]);

  return (
    <div className="p-6 lg:p-8 space-y-5">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2"><Sparkles className="w-7 h-7" style={{ color: GREEN }} />AI Assistant</h1>
        <p className="text-slate-500 mt-1">Answer pest questions, draft customer messages, and polish service reports.</p>
      </div>

      {status && !status.configured && (
        <Card className="p-3 border-amber-200 bg-amber-50 flex items-start gap-2 text-sm text-amber-800">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div><strong>Demo mode.</strong> No AI key is configured, so results use built-in templates. Add <code>AI_API_KEY</code> to <code>backend/.env</code> (Claude or OpenAI) to enable full AI.</div>
        </Card>
      )}

      <div className="flex gap-2">
        {tabs.map(([t, label, Ico]) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-1.5 transition ${tab === t ? 'text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
            style={tab === t ? { backgroundColor: GREEN } : {}}>
            <Ico className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      <Card className="p-5 border-slate-200">
        {tab === 'ask' && <AskTab />}
        {tab === 'draft' && <DraftTab />}
        {tab === 'summarize' && <SummarizeTab />}
        {tab === 'briefing' && <BusinessBriefing />}
        {tab === 'update' && <KnowledgeAutoUpdate />}
      </Card>
    </div>
  );
}
